import { useState } from 'react';

/**
 * Campo de clave con el ojito para ver lo que se escribe.
 *
 * POR QUÉ EXISTE: en un celular, a pleno sol y con una sola mano, escribir una clave a ciegas
 * termina en el mismo lugar siempre — "documento o clave incorrectos" sin saber si el error fue
 * la clave o el dedo. Las claves temporales que dicta el coordinador (`treid-y7sk`) mezclan
 * letras y números y se teclean mal seguido; poder mirarlas antes de enviar evita el rebote.
 *
 * Se hizo componente y no un `useState` copiado en cada pantalla porque el ojito tiene tres
 * detalles fáciles de olvidar de a uno: el botón NO puede ser `submit` (dentro de un formulario
 * enviaría el formulario al tocarlo), necesita `aria-label` porque su contenido es un dibujo sin
 * texto, y el `padding` derecho del input tiene que dejarle sitio o el ojito se monta encima de
 * los últimos caracteres — que es justo lo que la persona quiere leer.
 */

interface Props {
  id: string;
  /** Se manda al servidor con este nombre. Necesario donde el envío se lee con `FormData`. */
  name?: string;
  value: string;
  onChange: (valor: string) => void;
  /** `current-password` para ingresar, `new-password` para crear una. */
  autoComplete: 'current-password' | 'new-password';
  required?: boolean;
  className?: string;
}

export default function CampoClave({
  id,
  name,
  value,
  onChange,
  autoComplete,
  required = false,
  className = '',
}: Props) {
  const [visible, setVisible] = useState(false);

  return (
    <div className={`relative ${className}`}>
      <input
        id={id}
        name={name}
        // El tipo cambia, no el valor: nunca se copia la clave a otro sitio para mostrarla.
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        autoCapitalize="none"
        spellCheck={false}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        // `pr-11` reserva el ancho del botón: sin esto el ojito tapa el final de la clave.
        className="w-full rounded border border-gray-300 py-2 pl-3 pr-11 text-sm"
      />
      <button
        // `type="button"` es obligatorio: por defecto un botón dentro de un formulario ENVÍA,
        // así que tocar el ojito intentaría iniciar sesión con la clave a medio escribir.
        type="button"
        onClick={() => setVisible((v) => !v)}
        // El área táctil ocupa todo el alto del campo: un ojito de 16px no se acierta en la calle.
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-500"
        // El botón es un dibujo: sin esto, quien usa lector de pantalla oye "botón" y nada más.
        aria-label={visible ? 'Ocultar la clave' : 'Ver la clave'}
        aria-pressed={visible}
        // No entra en el recorrido del tabulador: quien navega con teclado ya ve lo que escribe
        // en su propia pantalla, y este botón entre la clave e "Ingresar" estorba el envío.
        tabIndex={-1}
      >
        {visible ? <IconoOjoTachado /> : <IconoOjo />}
      </button>
    </div>
  );
}

/* Los iconos van inline porque el proyecto no tiene librería de iconos y no vale traer una
   dependencia entera por dos dibujos. `aria-hidden`: el nombre accesible lo da el botón. */

function IconoOjo() {
  return (
    <svg
      aria-hidden="true"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12S18 18.5 12 18.5 2.5 12 2.5 12Z"
      />
      <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconoOjoTachado() {
  return (
    <svg
      aria-hidden="true"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 3l18 18M10.6 10.7a2 2 0 002.8 2.8M9.4 6.2A9.6 9.6 0 0112 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 01-2.4 3.3M6.3 7.9A17 17 0 002.5 12S6 18.5 12 18.5c1 0 1.9-.2 2.7-.5"
      />
    </svg>
  );
}
